import {
  AccountType,
  PermissionKey,
  UserPlanScope,
  UserPlanType,
} from '@pt/enums/user'
import type { UserInfo, UserPlan } from '@pt/types/user.type'
import { PlanType } from '@/common/permission'
import { MessageBox } from 'element-ui'
import { MessageBoxData } from 'element-ui/types/message-box'
import i18n from '@/lang'
import { differenceInDays } from 'date-fns'

export interface MakerStatus {
  // 是否订阅
  subscribed: boolean
  // 是否过期
  expired: boolean
  // 是否有权限访问，根据权限key判断
  allow: boolean
  // 套餐类型
  type?: UserPlanType
}

/**
 * 判断订阅是否过期了
 * 每个制作页面都要验证这个信息
 * 工具跳转到列表页面，到地址要看下是否有问题
 *
 * @returns boolean true 过期 false 未过期
 */
export const isSubscribeExpired = (
  userInfo: UserInfo,
  plan: UserPlan,
): boolean => {
  return plan.expired_time <= userInfo.system_time
}

export const getSubscribeStatus = (
  info: UserInfo,
  scopes: UserPlanScope[],
  key?: PermissionKey,
): MakerStatus => {
  const plan = info.plans?.find((p) => scopes.includes(p.scope))
  const subscribed = !!plan

  const expired = plan ? isSubscribeExpired(info, plan) : false

  const isAllow = key ? info.permissions.includes(key) : true
  // 是否允许展示只判断权限key为准，不依赖套餐
  const allow = isAllow
  return {
    subscribed,
    expired,
    allow,
    type: plan?.type,
  }
}

// 是否内部账户
export const isInternalAccount = (userInfo: UserInfo): boolean => {
  return userInfo.account_type === AccountType.Internal
}

// 是否支持批量制作
export const isVeBatch = (userInfo: UserInfo): boolean => {
  return userInfo.permissions.includes(PermissionKey.VeBatch)
}

const hasPlan = (playType: PlanType, plans: UserPlan[]): boolean => {
  return plans.some((t) => t.plan_type === playType)
}

// 判断当前用户是否开通了某个套餐，且在有效期内
export const hasPlanInEffect = (
  playType: PlanType,
  plans: UserPlan[],
  info: UserInfo,
): boolean => {
  return plans.some(
    (t) => t.plan_type === playType && !isSubscribeExpired(info, t),
  )
}

// 是否开通计费
export const isRecharge = (userInfo: UserInfo): boolean => {
  if (!userInfo.plans) return false

  return hasPlan(PlanType.Recharge, userInfo.plans)
}

// 开通的是订阅套餐
export const isSubscription = (userInfo: UserInfo): boolean => {
  if (!userInfo.plans) return false

  return hasPlan(PlanType.Subscription, userInfo.plans)
}

// 按量套餐少于该值时提示弹窗 (试玩剩余额度)
const RATION_MIN_LIMIT = 0
// 充值套餐 当订阅天数< 7
const RECHARGE_MIN_LIMIT = 7

/**
 * 检查按量套餐是否接近过期
 * true为接近过期
 */
export function checkRationPlanCloseExpire(
  plans: UserPlan[],
  limit = RATION_MIN_LIMIT,
): boolean {
  // 按量套餐
  const rationPlan = plans.find((t) => t.plan_type === PlanType.Ration)

  if (!rationPlan) return false

  return (
    plans.reduce((prev, curr) => {
      return (
        prev + curr.downloads_and_push[0] + curr.extra_downloads_and_push[0]
      )
    }, 0) < limit
  )
}

/**
 * 检查充值套餐是否接近过期
 * true为接近过期
 */
export function checkRechargePlanCloseExpire(
  plans: UserPlan[],
  limit = RECHARGE_MIN_LIMIT,
): boolean {
  // 充值套餐
  const rechargePlan = plans.find((t) => t.plan_type === PlanType.Recharge)

  if (!rechargePlan) return false

  return differenceInDays(rechargePlan.expired_time * 1000, Date.now()) < limit
}

/** 检查定量\订阅套餐试玩剩余额度、当订阅天数 */
export function checkPlanCloseExpire(userInfo: UserInfo) {
  if (!userInfo.plans) return Promise.reject()

  if (checkRechargePlanCloseExpire(userInfo.plans)) {
    return showExpirationReminderMessage()
  }

  if (checkRationPlanCloseExpire(userInfo.plans)) {
    return showInsufficientQuotaMessage()
  }

  return Promise.resolve()
}

// 显示额度不足弹窗
export function showInsufficientQuotaMessage(): ReturnType<typeof showMessage> {
  return showMessage(
    i18n.t('playTurbo.profile.plan.insufficientQuota').toString(),
    i18n.t('playTurbo.profile.plan.insufficientQuotaTips').toString(),
  )
}

// 显示到期弹窗
export function showExpirationReminderMessage(): ReturnType<
  typeof showMessage
> {
  return showMessage(
    i18n.t('playTurbo.profile.plan.expirationReminder').toString(),
    i18n.t('playTurbo.profile.plan.expirationReminderTips').toString(),
  )
}

export function showMessage(
  title: string,
  message: string,
): Promise<MessageBoxData> {
  return MessageBox({
    customClass: 'pt-custom-confirm-message',
    title,
    message,
    dangerouslyUseHTMLString: true,
    type: 'warning',
    showCancelButton: true,
    showConfirmButton: false,
    cancelButtonText: i18n
      .t('playTurbo.profile.plan.message.confirm')
      .toString(),
    cancelButtonClass: 'el-button--primary',
    center: true,
  })
}
